'use client'

import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons'
import { useIntl } from 'react-intl'

import {
  Button,
  CurrencyStringDollars,
  FormattedMessage,
  Modal,
} from '@b-stock/fe-core/bstock-react'

import {
  ConfirmationModalActions as Actions,
  ConfirmationModalBody as Body,
  ConfirmationModalNoteWarning as NoteWarning,
  ConfirmationModalWarningIcon as WarningIcon,
  StyledConfirmationModal as StyledModal,
} from './WatchlistConfirmationModalShell'

type Props = {
  isOpen: boolean
  listingTitle: string
  storefrontName?: string
  // undefined = no active bid; number > 0 = active bid amount to surface.
  bidAmount?: number
  currency?: string
  hasNote: boolean
  isPending: boolean
  onCancel: () => void
  onConfirm: () => void
}

export const WatchlistRemoveConfirmationModal = ({
  isOpen,
  listingTitle,
  storefrontName,
  bidAmount,
  currency,
  hasNote,
  isPending,
  onCancel,
  onConfirm,
}: Props) => {
  const { formatMessage } = useIntl()

  if (!isOpen) return null

  // Use the no-storefront variant when we don't have one — otherwise the base
  // template renders empty parentheses next to the title.
  const bodyBase = storefrontName
    ? formatMessage(
        { id: 'Common.Watchlist.removeConfirmation.bodyBase' },
        { title: listingTitle, storefrontName }
      )
    : formatMessage(
        { id: 'Common.Watchlist.removeConfirmation.bodyBaseNoStorefront' },
        { title: listingTitle }
      )

  const bidPart =
    bidAmount && bidAmount > 0 ? (
      <FormattedMessage
        id="Common.Watchlist.removeConfirmation.bodyBidPart"
        values={{
          amount: (
            <CurrencyStringDollars
              value={bidAmount}
              currency={currency ?? 'USD'}
            />
          ),
        }}
      />
    ) : (
      <FormattedMessage id="Common.Watchlist.removeConfirmation.bodyNoBidPart" />
    )

  return (
    <StyledModal closeModal={onCancel}>
      <Modal.Header>
        <FormattedMessage id="Common.Watchlist.removeConfirmation.title" />
      </Modal.Header>
      <Modal.Content>
        <Body>
          {bodyBase} {bidPart}
        </Body>
        {hasNote && (
          <NoteWarning data-testid="watchlist-remove-note-warning">
            <WarningIcon icon={faTriangleExclamation} aria-hidden="true" />
            <span>
              <FormattedMessage id="Common.Watchlist.removeConfirmation.noteWarning" />
            </span>
          </NoteWarning>
        )}
      </Modal.Content>
      <Actions>
        <Button
          type="button"
          appearance="secondary"
          onClick={onCancel}
          disabled={isPending}
        >
          <FormattedMessage id="Common.Watchlist.removeConfirmation.cancel" />
        </Button>
        <Button
          type="button"
          appearance="dangerousPrimary"
          onClick={onConfirm}
          disabled={isPending}
          data-testid="watchlist-remove-confirm-button"
        >
          <FormattedMessage id="Common.Watchlist.removeConfirmation.confirm" />
        </Button>
      </Actions>
    </StyledModal>
  )
}
